// src/modules/app-views/invoices/components/CardHeaderInvoiceTags.jsx
// *******************************************************************************
// INVOICE CARD HEADER TAGS
// *******************************************************************************
// Displays validation, status, document type, and operation type tags.
// Shows appropriate values for both new and existing documents.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tag, Tooltip, Form } from 'antd';
import { DownloadOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import DocumentValidationTag from 'components/core/DocumentValidationTag';
import InvoiceStatusTag from 'modules/app-views/invoices/components/InvoiceStatusTag';
import InvoiceTypesTag from 'modules/app-views/invoices/components/InvoiceTypesTag';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RESOURCE_NFE = 'moduleresources_nfe';
const RESOURCE_NFCE = 'moduleresources_nfce';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CardHeaderInvoiceTags = ({ form, data: dataProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const currentPage = useZustand?.('module', 'currentPage', false);
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Use dataProp if provided (preview), otherwise use currentPage.data (document page)
  const data = dataProp || currentPage?.data;
  const isNew = !data?._id || data?._id === 'add';

  // Check subscription resources for NFe/NFCe
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);

  // Watch form values reactively for new documents (Form.useWatch triggers re-render on change)
  const formDocumentType = Form.useWatch('documentType', form);
  const formOperationType = Form.useWatch('operationType', form);

  // Determine effective document type when form value is undefined (e.g., when only one resource is available)
  const effectiveDocumentType = formDocumentType || (hasNfceResource && !hasNfeResource ? 'nfce' : 'nfe');

  // For new documents, use effective form value; for existing, use data
  const documentType = isNew ? effectiveDocumentType : data?.documentType;
  const operationType = isNew ? formOperationType : data?.operationType;
  const hasValidation = !!data?.documentValidation;

  // Operation type: exit = SAÍDA, entry = ENTRADA
  const isExit = operationType === 'exit';
  const operationLabel = isExit ? translateX('operation_exit') : translateX('operation_entry');
  const OperationIcon = isExit ? UploadOutlined : DownloadOutlined;
  const operationColor = isExit ? 'cyan' : 'orange';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Validation Tag: gray icon for new/unvalidated, normal for validated */}
      {hasValidation ? (
        <DocumentValidationTag data={data} style={{ display: 'flex' }} />
      ) : (
        <Tooltip title={translateX('invoice_not_validated')} placement="top">
          <ExclamationCircleOutlined style={{ fontSize: 16, color: '#bfbfbf', display: 'flex' }} />
        </Tooltip>
      )}

      {/* Status Tag: draft for new documents */}
      <Tooltip title={translateX('status')}>
        <span>
          <InvoiceStatusTag data={isNew ? { status: 'draft' } : data} style={{ margin: 0 }} />
        </span>
      </Tooltip>

      {/* Document Type Tag */}
      <Tooltip title={translateX('document_type')}>
        <span>
          <InvoiceTypesTag data={isNew ? { documentType } : data} style={{ margin: 0 }} />
        </span>
      </Tooltip>

      {/* Operation Type Tag */}
      <Tooltip title={translateX('operation_type')}>
        <Tag color={operationColor} style={{ margin: 0 }}>
          <OperationIcon style={{ marginRight: 4 }} />
          {operationLabel}
        </Tag>
      </Tooltip>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderInvoiceTags;

// *******************************************************************************
// HELPERS
// *******************************************************************************
